/* Well & Hale section kit (wh-*): gallery, package picker, carousels, reviews and sticky bar. */
(() => {
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());

  /* Add to cart without leaving the page: post to /cart/add with the cart drawer's section, then fire Horizon's
     cart event so the drawer re-renders (and slides open when it was closed). Returns false when the theme's cart
     drawer or routes are missing, so the caller can fall back to a normal form submit. Pass `lines` when the body
     adds several items at once (items[n][id] fields). */
  async function ajaxAdd(body, source, el, lines) {
    // Horizon declares `Theme` with const in a classic script: a global binding, not a window property.
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null; // eslint-disable-line no-undef
    if (!document.querySelector('cart-drawer-component') || !routes?.cart_add_url) return false;
    const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    if (ids.length) body.append('sections', ids.join(','));
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    el.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines || [{ merchandiseId: String(body.get('id')), quantity: Number(body.get('quantity')) || 1 }],
      promise: deferred.promise,
    }));
    const res = await fetch(routes.cart_add_url, {
      method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
    }).then((r) => r.json());
    const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: res.sections, didError: Boolean(res.status), source },
    });
    if (res.status) alert(res.description || res.message || 'Could not add to cart.');
    return true;
  }

  /* Buttons that open a native <dialog> by id (slipper size guide in the buy box). */
  document.addEventListener('click', (ev) => {
    const btn = ev.target.closest('[data-whp-dialog-open]');
    if (!btn) return;
    const dlg = document.getElementById(btn.dataset.whpDialogOpen);
    if (dlg?.showModal) { ev.preventDefault(); dlg.showModal(); }
  });
  document.addEventListener('click', (ev) => { if (ev.target.matches?.('dialog.whp-size')) ev.target.close(); }); // backdrop click closes

  /* Side cart "add one more" button (snippets/wh-cart-extras.liquid). The drawer re-renders, so delegate. */
  document.addEventListener('click', async (ev) => {
    const btn = ev.target.closest?.('[data-whc-add]');
    if (!btn) return;
    ev.preventDefault();
    btn.disabled = true;
    const body = new FormData();
    body.append('id', btn.dataset.whcAdd);
    body.append('quantity', '1');
    try { await ajaxAdd(body, 'wh-cart-upsell', btn); } catch (err) { console.warn('[wh] cart upsell failed', err); btn.disabled = false; }
  });

  /* Swipe track with prev/next buttons, used by the gallery and the carousels. */
  function scrollTrack(track, dir) {
    const first = track.firstElementChild;
    if (!first) return;
    const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0);
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  function initGallery(gal) {
    const track = gal.querySelector('[data-track]');
    if (!track) return;
    const slides = [...track.children];
    const thumbs = [...gal.querySelectorAll('[data-go]')];
    const dotsWrap = gal.querySelector('[data-dots]');
    const dots = [];
    if (dotsWrap && slides.length > 1) {
      slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `Show slide ${i + 1}`);
        b.addEventListener('click', () => go(i));
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }
    const go = (i) => track.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
    const setActive = (i) => {
      thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
      dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
    };
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.go))));
    gal.querySelector('[data-prev]')?.addEventListener('click', () => scrollTrack(track, -1));
    gal.querySelector('[data-next]')?.addEventListener('click', () => scrollTrack(track, 1));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting && e.intersectionRatio > 0.6) setActive(slides.indexOf(e.target)); });
    }, { root: track, threshold: [0.6] });
    slides.forEach((s) => io.observe(s));
    setActive(0);
  }

  function initCarousel(root) {
    const track = root.querySelector('[data-track]');
    if (!track) return;
    root.querySelector('[data-prev]')?.addEventListener('click', () => scrollTrack(track, -1));
    root.querySelector('[data-next]')?.addEventListener('click', () => scrollTrack(track, 1));
    const vids = track.querySelectorAll('video[data-autoplay]');
    if (!vids.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) { v.preload = 'auto'; v.play().catch(() => {}); } else { v.pause(); }
      });
    }, { threshold: 0.5 });
    vids.forEach((v) => io.observe(v));
  }

  /* Package tabs + second option select drive the hidden variant id and the price box. */
  function initBuyBox(main) {
    const data = main.querySelector('[data-whp-variants]');
    const form = main.querySelector('[data-whp-form]');
    if (!data || !form) return;
    const variants = JSON.parse(data.textContent);
    const idInput = form.querySelector('[data-whp-id]');
    const pkgs = [...form.querySelectorAll('[data-pkg]')];
    const opt2 = form.querySelector('[data-whp-opt2]');
    const opt2Wrap = form.querySelector('[data-whp-opt2-wrap]');
    const rows = [...form.querySelectorAll('[data-show-for]')];
    const addBtn = form.querySelector('[data-whp-add]');
    const addLabel = form.querySelector('[data-whp-add-label]');
    const buyLabel = addLabel ? addLabel.textContent : '';
    const current = variants.find((v) => String(v.id) === idInput.value) || variants[0];
    let pkg = current.o1;

    const update = () => {
      const pkgIndex = pkgs.findIndex((b) => b.dataset.pkg === pkg) + 1;
      pkgs.forEach((b) => {
        const on = b.dataset.pkg === pkg;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      rows.forEach((r) => { r.hidden = !(r.dataset.showFor === 'all' || r.dataset.showFor === String(pkgIndex)); });

      let choices = variants.filter((v) => v.o1 === pkg);
      if (opt2) {
        // Rebuild the list so it only offers values that exist for this package (Safari ignores hidden options).
        const allowed = [...new Set(choices.map((v) => v.o2))];
        const keep = allowed.includes(opt2.value) ? opt2.value : allowed[0];
        opt2.replaceChildren(...allowed.map((val) => new Option(val, val, false, val === keep)));
        if (opt2Wrap) opt2Wrap.hidden = allowed.length < 2;
        choices = choices.filter((v) => v.o2 === keep);
      }
      const v = choices[0];
      if (!v) return;
      idInput.value = v.id;
      document.querySelectorAll('[data-whp-price]').forEach((el) => { el.textContent = v.price; });
      const cmp = main.querySelector('[data-whp-compare]');
      const regRow = main.querySelector('[data-whp-regular-row]');
      const save = main.querySelector('[data-whp-save]');
      if (cmp) cmp.textContent = v.compare || '';
      if (regRow) regRow.hidden = !v.compare;
      if (save) { save.hidden = !v.save; save.textContent = v.save ? `${save.dataset.label} ${v.save}` : ''; }
      if (addBtn) { addBtn.disabled = !v.available; if (addLabel) addLabel.textContent = v.available ? buyLabel : 'Sold out'; }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(null, '', url);
    };

    pkgs.forEach((b) => b.addEventListener('click', () => { pkg = b.dataset.pkg; update(); }));
    opt2?.addEventListener('change', update);
    form.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => {
      const q = form.querySelector('[data-whp-qty]');
      q.value = Math.max(1, (parseInt(q.value, 10) || 1) + Number(b.dataset.qty));
    }));
    update();

    /* Add to cart in place (see ajaxAdd); a normal submit is the fallback. */
    form.addEventListener('submit', async (ev) => {
      if (!document.querySelector('cart-drawer-component')) return;
      ev.preventDefault();
      if (addBtn?.disabled) return;
      addBtn?.classList.add('is-loading');
      try {
        if (!(await ajaxAdd(new FormData(form), 'wh-pdp', form))) HTMLFormElement.prototype.submit.call(form);
      } catch (err) {
        console.warn('[wh] ajax add failed, submitting normally', err);
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        addBtn?.classList.remove('is-loading');
      }
    });

    initSticky(main, form, addBtn);
  }

  /* Sticky bar: shows once the main button scrolls away, and submits the same form. */
  function initSticky(main, form, addBtn) {
    const sticky = document.querySelector('[data-whp-sticky]');
    if (sticky && addBtn) {
      sticky.hidden = false;
      sticky.style.transform = 'translateY(110%)';
      sticky.style.transition = 'transform .25s ease';
      // Show once the main button has scrolled above the screen, hide again over the footer.
      let pastButton = false;
      let footerVisible = false;
      const render = () => { sticky.style.transform = pastButton && !footerVisible ? 'translateY(0)' : 'translateY(110%)'; };
      new IntersectionObserver(([e]) => { pastButton = !e.isIntersecting && e.boundingClientRect.top < 0; render(); }).observe(addBtn);
      const footer = document.querySelector('.shopify-section-group-footer-group, footer');
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; render(); }).observe(footer);
      sticky.querySelector('[data-whp-sticky-buy]')?.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(addBtn); else addBtn.click();
      });
    }
    document.querySelectorAll('[data-whp-scroll-buy]').forEach((a) => a.addEventListener('click', (ev) => {
      ev.preventDefault();
      main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
  }

  /* Refill plan buy box (snippets/wh-plan-buybox): colour swatches, plan cards, an add-on and a one-time switch.
     With a plan the device line gets the store's automatic plan discount, so the summary takes data-head-off off it. */
  function initPlan(main) {
    const root = main.querySelector('[data-whp-plan]');
    const form = root?.querySelector('[data-whp-plan-form]');
    if (!form) return;
    const off = Number(root.dataset.headOff) || 0;
    const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: root.dataset.currency || 'USD', minimumFractionDigits: cents % 100 ? 2 : 0,
    }).format(cents / 100);
    const q = (sel) => root.querySelector(sel);
    const colours = [...form.querySelectorAll('input[name="items[0][id]"]')];
    const plans = [...form.querySelectorAll('input[name="items[1][id]"]')];
    const planQty = q('[data-plan-qty]');
    const sp = q('[data-plan-sp]');
    const addon = q('[data-plan-addon]');
    const addonQty = q('[data-plan-addon-qty]');
    const step2 = q('[data-plan-supply-step]');
    const onceNote = q('[data-plan-onetime-note]');
    const sw = q('[data-plan-switch]');
    const btn = q('[data-whp-add]');
    const btnLabel = q('[data-plan-btn]');
    const devImg = q('.whp-plan__device-img img');
    let once = !plans.length;

    const update = () => {
      const colour = colours.find((c) => c.checked) || colours[0];
      const plan = plans.find((p) => p.checked) || plans[0];
      const headFull = Number(colour?.dataset.price) || 0;
      const discount = once ? 0 : Math.min(off, headFull);
      const supply = once || !plan ? 0 : Number(plan.dataset.price) || 0;
      const extra = addon?.checked ? Number(addon.dataset.price) || 0 : 0;
      const total = headFull - discount + supply + extra;

      const name = q('[data-plan-colour-name]');
      if (name) name.textContent = colour?.dataset.name || '';
      plans.forEach((p) => { p.disabled = once; });
      if (planQty) planQty.disabled = once;
      if (step2) step2.hidden = once;
      if (onceNote) onceNote.hidden = !once;
      if (sp) { sp.value = plan?.dataset.sp || ''; sp.disabled = once || !sp.value; }
      if (addonQty) addonQty.disabled = !addon.checked;

      const headWas = q('[data-sum-head-was]');
      if (headWas) { headWas.hidden = !discount; headWas.textContent = money(headFull); }
      q('[data-sum-head]').textContent = money(headFull - discount);
      const devWas = q('[data-plan-dev-was]');
      if (devWas) { devWas.hidden = !discount; devWas.textContent = money(headFull); }
      const devNow = q('[data-plan-dev-now]');
      if (devNow) devNow.textContent = money(headFull - discount);
      const note = q('[data-plan-note]');
      if (note) note.hidden = once;
      const supplyRow = q('[data-sum-supply-row]');
      if (supplyRow) {
        supplyRow.hidden = once;
        q('[data-sum-supply-name]').textContent = plan?.dataset.name || '';
        q('[data-sum-supply]').textContent = money(supply);
      }
      const addonRow = q('[data-sum-addon-row]');
      if (addonRow) addonRow.hidden = !extra;
      const was = q('[data-sum-was]');
      if (was) { was.hidden = !discount; was.textContent = money(total + discount); }
      q('[data-sum-total]').textContent = money(total);
      if (btnLabel) btnLabel.textContent = `${once ? btn.dataset.labelOnce : btn.dataset.labelPlan} - ${money(total)}`;
      document.querySelectorAll('[data-whp-price]').forEach((el) => { el.textContent = money(total); });
      if (sw) sw.textContent = once ? sw.dataset.labelPlan : `${sw.dataset.labelOnce} ${money(headFull)}`;
      if (colour) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', colour.value);
        history.replaceState(null, '', url);
      }
    };

    colours.forEach((c) => c.addEventListener('change', () => {
      update();
      if (devImg && c.dataset.img) { devImg.removeAttribute('srcset'); devImg.src = c.dataset.img; }
      const slide = Number(c.dataset.slide);
      if (slide >= 0) main.querySelector(`[data-whp-gallery] [data-go="${slide}"]`)?.click(); // data-slide counts the video slide
    }));
    plans.forEach((p) => p.addEventListener('change', update));
    addon?.addEventListener('change', update);
    sw?.addEventListener('click', () => { once = !once; update(); });
    update();

    /* Add every line in one request (see ajaxAdd); a normal submit to /cart/add is the fallback. */
    form.addEventListener('submit', async (ev) => {
      if (!document.querySelector('cart-drawer-component')) return;
      ev.preventDefault();
      if (btn?.disabled) return;
      btn?.classList.add('is-loading');
      const body = new FormData(form);
      const lines = [];
      for (const [key, value] of body) {
        const m = key.match(/^items\[(\d+)\]\[id\]$/);
        if (m) lines.push({ merchandiseId: String(value), quantity: Number(body.get(`items[${m[1]}][quantity]`)) || 1 });
      }
      try {
        if (!(await ajaxAdd(body, 'wh-plan', form, lines))) HTMLFormElement.prototype.submit.call(form);
      } catch (err) {
        console.warn('[wh] plan add failed, submitting normally', err);
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        btn?.classList.remove('is-loading');
      }
    });

    initSticky(main, form, btn);
  }

  ready(() => {
    document.querySelectorAll('[data-whp-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-whp-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-whp-main]').forEach(initBuyBox);
    document.querySelectorAll('[data-whp-main]').forEach(initPlan);
    document.querySelectorAll('[data-whp-more]').forEach((btn) => btn.addEventListener('click', () => {
      btn.closest('.whp-reviews')?.querySelectorAll('[data-more]').forEach((r) => { r.hidden = false; });
      btn.remove();
    }));
  });
})();
